"use client";

import { useEffect, useRef, useState, type FC } from "react";
import { ListBulletIcon, MapIcon } from "@heroicons/react/24/outline";
import type { PractitionerSearchDto } from "cmw-client";
import { Button } from "@/components/button";
import {
  AllFiltersButton,
  filterCount,
  NO_FILTERS,
  toQuery,
} from "@/components/directory-filter-bar";
import { DirectorySearchHero } from "@/components/directory-search-hero";
import { PractitionerList } from "@/components/practitioner-list";
import { hidePublicHeader } from "@/components/public-header";
import { useTurnstile } from "@/components/turnstile";
import { useCurrentLocation } from "@/features/maps";
import { PractitionerMapsView } from "@/features/maps-v2";
import { LlmFilterBar } from "@/components/llm-filter-bar";
import { useLlmFilterSearch } from "@/hooks/use-llm-filter-search";
import { useInfinitePublicPractitioners } from "@/hooks/use-public-practitioners";
import { useScrollFlag } from "@/hooks/use-scroll-flag";
import { useTaxonomy } from "@/hooks/use-taxonomy";
import { cn } from "@/lib/cn";
import { useDirectorySearch, type ViewMode } from "./use-directory-search";
import { useHideHeaderOnMapSnap } from "./use-hide-header-on-map-snap";

const PAGE_SIZE = 12;
const LOAD_MORE_PAGE_SIZE = 20;
const SEARCH_INPUT_ID = "directory-location-search";
const MIN_VISIBLE_PX = 24;

/* The map places only practitioners with an exact address; those who gave just a suburb are missing.
   Set to false to hide both map switches again, which also keeps an old `?view=map` link on the list. */
const MAP_VIEW_ENABLED = true;

const MAP_PILL =
  "rounded-circle font-normal text-neutral-black shadow-md hover:border-brand-dark-blue hover:bg-brand-dark-blue hover:text-neutral-white";

/* Not an IntersectionObserver: after the snap the bar's edge touches the viewport edge, which still counts as intersecting. */
const isOffscreen = (el: HTMLElement) => {
  const headerOffset =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--public-header-offset")) || 0;
  const rect = el.getBoundingClientRect();
  return rect.bottom - headerOffset <= MIN_VISIBLE_PX || rect.top >= window.innerHeight - MIN_VISIBLE_PX;
};

export const HomePage: FC = () => {
  const { filters, location, viewMode, setFilters, setLocation, setViewMode } = useDirectorySearch();

  const { locate, isLocating } = useCurrentLocation();

  const { getAll } = useTaxonomy();
  const taxonomy = Object.fromEntries(getAll().map((entry) => [entry.fieldType, entry.options]));

  /* The search endpoint's product is the filter object: we push it into the URL through the
     same setter the manual controls use, so the existing query, pagination and map all pick
     it up with no idea an LLM was involved. */
  const [searchResult, setSearchResult] = useState<PractitionerSearchDto | undefined>();
  const { turnstileRef, takeTurnstileToken } = useTurnstile();

  const handleNearMe = async () => {
    const result = await locate();
    if (result) setLocation(result);
  };

  const llmSearch = useLlmFilterSearch((result) => {
    setSearchResult(result);
    if (result.interpreted) {
      setFilters({ ...NO_FILTERS, ...result.filters });
      /* The query asked for a location ("near me", a suburb): run the same
         geolocation flow as the Near me button, so the results come back sorted
         around the searcher without another click. */
      if (result.nearMe) void handleNearMe();
    }
  }, takeTurnstileToken);

  const query = toQuery(filters, location?.coordinates);
  const { practitioners, total, hasMore, loadMore, isLoadingMore, isLoading, isError, error } =
    useInfinitePublicPractitioners(query, PAGE_SIZE, LOAD_MORE_PAGE_SIZE);

  const activeFilters = filterCount(filters);
  const showCount = (activeFilters > 0 || location !== null) && !isLoading;
  const isMap = MAP_VIEW_ENABLED && viewMode === "map";

  const resultRowRef = useRef<HTMLDivElement>(null);
  const showMapToolbar = useScrollFlag(resultRowRef, isMap, isOffscreen);

  const sectionRef = useRef<HTMLElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const pendingScroll = useRef<ViewMode | null>(null);
  const changeViewMode = (next: ViewMode) => {
    pendingScroll.current = next;
    setViewMode(next);
  };
  useHideHeaderOnMapSnap(mapRef, isMap);
  useEffect(() => {
    if (pendingScroll.current !== viewMode) return;
    pendingScroll.current = null;
    const target = viewMode === "map" ? mapRef.current : sectionRef.current;
    target?.scrollIntoView({ behavior: "instant", block: "start" });
    hidePublicHeader();
  }, [viewMode]);

  const focusSearch = () => {
    const input = document.getElementById(SEARCH_INPUT_ID);
    if (!input) return;

    // scrollIntoView would scroll the overflow-hidden hero itself, shifting its artwork.
    const { top, height } = input.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + top - (window.innerHeight - height) / 2, behavior: "smooth" });
    input.focus({ preventScroll: true });
  };

  const resultCount = showCount ? (
    <p className="text-16 text-neutral-black/70">
      Showing{" "}
      <span className="font-semibold text-neutral-black">
        {total.toLocaleString()} practitioner{total === 1 ? "" : "s"}
      </span>{" "}
      {location ? `near ${location.address}` : "matching"}
    </p>
  ) : null;

  const mapToolbarClassName = cn(
    MAP_PILL,
    "transition-opacity duration-200",
    !showMapToolbar && "pointer-events-none opacity-0",
  );

  return (
    <>
      <DirectorySearchHero
        inputId={SEARCH_INPUT_ID}
        location={location}
        onLocationChange={setLocation}
        onClear={() => setLocation(null)}
      />
      <section ref={sectionRef} className="bg-background-grey py-32 lg:pt-48 lg:pb-120">
        <div className="-mt-8 bg-background-grey py-8 md:-mt-16 md:pt-16 md:pb-24">
          <div className="page-width">
            <LlmFilterBar
              filters={filters}
              onChange={setFilters}
              taxonomy={taxonomy}
              viewMode={isMap ? "map" : "list"}
              onViewModeChange={MAP_VIEW_ENABLED ? changeViewMode : undefined}
              onSearch={llmSearch.search}
              isSearching={llmSearch.isSearching}
              searchResult={searchResult}
              isSearchError={llmSearch.isError}
              turnstileRef={turnstileRef}
            />
          </div>
        </div>

        <div
          ref={resultRowRef}
          className={cn(
            "page-width max-md:border-t max-md:border-neutral-light-grey max-md:pt-16 md:pb-24",
            resultCount && "max-md:pt-24 max-md:pb-16",
          )}
        >
          <div className="flex items-center justify-between gap-16">
            {resultCount ?? <span />}
            {/* Below `md` the switch is part of the filter bar itself (Figma 1068:31085). */}
            {MAP_VIEW_ENABLED && (
              <Button
                variant="secondary-white"
                size="s"
                icon={isMap ? <ListBulletIcon className="size-18" /> : <MapIcon className="size-18" />}
                className="max-md:hidden"
                onClick={() => changeViewMode(isMap ? "list" : "map")}
              >
                {isMap ? "List view" : "Map view"}
              </Button>
            )}
          </div>
        </div>

        {isMap ? (
          <div ref={mapRef}>
            <PractitionerMapsView
              query={query}
              center={location?.coordinates ?? null}
              toolbar={
                <>
                  <AllFiltersButton
                    filters={filters}
                    onChange={setFilters}
                    taxonomy={taxonomy}
                    className={mapToolbarClassName}
                  />
                  <Button
                    variant="secondary-white"
                    size="s"
                    icon={<ListBulletIcon className="size-18" />}
                    className={mapToolbarClassName}
                    onClick={() => changeViewMode("list")}
                  >
                    List view
                  </Button>
                </>
              }
            />
          </div>
        ) : (
          <div className="page-width space-y-45">
            <PractitionerList
              practitioners={practitioners}
              isPending={isLoading}
              isError={isError}
              error={error}
              isLoadingMore={isLoadingMore}
              isFiltered={activeFilters > 0}
              isLocated={location !== null}
              onClearFilters={() => setFilters(NO_FILTERS)}
              onSearch={focusSearch}
              onNearMe={() => void handleNearMe()}
              isLocating={isLocating}
            />

            {hasMore && (
              <div className="flex flex-col items-center gap-8">
                <Button
                  variant="secondary-white"
                  size="l"
                  onClick={() => loadMore()}
                  disabled={isLoadingMore}
                  className="w-full sm:w-auto"
                >
                  {isLoadingMore ? "Loading…" : "Load more"}
                </Button>
                <p className="text-14 text-neutral-black/70">
                  Showing {practitioners.length} of {total.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
};
